// Content for /aba-billing-platform. Presentation (layout, styles, artwork, animations) lives in
// the shared template at `common/abaBillingPlatform`; this file is ONLY the page's copy.

export const HERO = {
    badgeA: 'ALL-IN-ONE',
    badgeB: 'ABA SOFTWARE',
    titleLead: 'ABA billing automations, on top of automations, ',
    titleAccent: 'on top of automations.',
    subtitle:
        'Eligibility checks, claim submissions, ERAs, payment posting, error checks — automated and stacked end to end. TherapyLake handles the billing busywork so your ABA team doesn’t have to.',
    trust: ['Built for ABA', 'Integrated clearinghouse', 'Optional RCM team'],
};

// Feature sections — text only (each section's artwork is its own illustration component).
export const FEATURES = [
    {
        id: 'guardrails',
        eyebrow: 'PREVENT ISSUES EARLY',
        title: 'Billing guardrails that help catch mistakes before they cost you',
        body: 'TherapyLake helps catch billing issues early, starting with scheduling and continuing through claims. Built-in workflows, warnings, and error checks guide teams before mistakes turn into lost revenue.',
        bullets: [
            'Help prevent billing mistakes starting at scheduling',
            'Catch missing or incorrect details before claims move forward',
            'Guide staff with built-in workflows, alerts, and error checks',
            'Reduce rework caused by authorization, appointment, or documentation issues',
        ],
        reversed: false,
    },
    {
        id: 'rcm-agents',
        eyebrow: 'IMPROVE CONTINUOUSLY',
        title: 'Built with experienced RCM agents and billing teams',
        body: 'TherapyLake is shaped by experienced RCM agents and billing professionals. We keep improving the billing experience for small clinics, growing teams, and large ABA practices.',
        bullets: [
            'Built with feedback from experienced RCM agents and billing teams',
            'Continuously improved around real ABA billing workflows',
            'Supports small clinics, growing teams, and larger practices',
            'Help billing teams work with clearer processes and fewer manual gaps',
        ],
        reversed: true,
    },
    {
        id: 'automate',
        eyebrow: 'AUTOMATE THE FLOW',
        title: 'Automated claim submissions, ERAs, and payment posting',
        body: 'Claim submissions, ERAs, and payment posting are automated and integrated with clearinghouse workflows. Teams can move from submission to payment with less manual work.',
        bullets: [
            'Automate claim submissions through integrated clearinghouse workflows',
            'Connect ERAs directly into billing and payment workflows',
            'Streamline payment posting and reconciliation activity',
            'Reduce manual entry and disconnected billing handoffs',
        ],
        reversed: false,
    },
    {
        id: 'eligibility',
        eyebrow: 'VERIFY BEFORE YOU SERVE',
        title: 'Built-in eligibility checks to help prevent revenue loss',
        body: 'TherapyLake helps teams verify eligibility before services are rendered. This helps prevent avoidable denials, coverage issues, and lost revenue.',
        bullets: [
            'Check eligibility before services are rendered',
            'Help prevent sessions for clients who may no longer be eligible',
            'Reduce avoidable denials tied to coverage issues',
            'Protect revenue by catching eligibility problems earlier',
        ],
        reversed: true,
    },
    {
        id: 'reports',
        eyebrow: 'SEE THE HEALTH OF RECEIVABLES',
        title: 'Custom reports, KPIs, and a transparent receivables dashboard',
        body: 'Custom reports, KPIs, and a transparent receivables dashboard help teams track billing health, manage claims, and stay organized in one place.',
        bullets: [
            'Track custom billing reports and revenue KPIs',
            'Monitor the health of receivables with greater transparency',
            'Manage claims, follow-up, and payment activity in one dashboard',
            'Help teams stay organized across open claims and outstanding balances',
        ],
        reversed: false,
    },
    {
        id: 'connected',
        eyebrow: 'CONNECTED TO THE WHOLE PRACTICE',
        title: 'Billing connected to authorizations, appointments, and sessions',
        body: 'TherapyLake keeps billing connected to authorizations, appointments, sessions, notes, and client records. When issues happen, teams can trace them back faster.',
        bullets: [
            'Trace billing issues back to authorizations, appointments, and session records',
            'Keep documentation, scheduling, and billing connected in one system',
            'Find mistakes faster when claims need review or correction',
            'Reduce back-and-forth between clinical, admin, and billing teams',
        ],
        reversed: true,
    },
];

export const RCM = {
    eyebrow: 'NEED EXTRA HELP?',
    title: 'Optional RCM services for TherapyLake clients',
    body: 'TherapyLake clients can also access optional RCM services at highly competitive rates. Get experienced billing support while keeping workflows connected to the platform.',
    bullets: [
        'Access optional RCM services available to TherapyLake clients',
        'Get help from experienced billing and revenue cycle professionals',
        'Reduce the burden on internal billing teams',
        'Keep managed billing work connected to the same system your practice uses daily',
    ],
    cta: 'Talk to our RCM team',
};

export const FINAL_CTA = {
    badge: 'ALL-IN-ONE ABA SOFTWARE',
    title: 'Run your ABA billing the way it should’ve worked all along.',
    body: 'Catch mistakes early, automate claims and ERAs, and keep billing connected to the rest of your practice — with an optional RCM team when you want the help.',
    primary: 'Start 2 months free',
    secondary: 'Book a demo',
};

/** Grouped content passed to the shared `AbaBillingPlatformFragment`. */
export const CONTENT = { hero: HERO, features: FEATURES, rcm: RCM, finalCta: FINAL_CTA };
